"use client"

import { useState, useRef, useEffect } from "react"
import { ClipboardList, Check, Camera, X, ChevronRight, Clock, ChevronLeft, PenTool, Image, CreditCard, FileText, Paperclip, Loader2 } from "lucide-react"
import { cn } from "@/lib/utils"
import { useSubmitInspectionReport } from "@/hooks/useUrgently"
import { useDriverStateV2 } from "@/hooks/useDriverStateV2"
import { DocumentTypeSelector } from "@/components/common/DocumentTypeSelector"
import { CapturedPhoto, DocumentType, DOCUMENT_TYPE_LABELS } from "@/types/document"

type InspectionType = "pre-trip" | "post-trip"

const DOCUMENT_TYPE_ICONS: Record<DocumentType, React.ReactNode> = {
  [DocumentType.CUSTOMER_SIGNATURE]: <PenTool className="w-4 h-4" />,
  [DocumentType.CUSTOMER_VEHICLE_PHOTO]: <Image className="w-4 h-4" />,
  [DocumentType.LICENSE_PLATE_PHOTO]: <CreditCard className="w-4 h-4" />,
  [DocumentType.RECEIPT]: <FileText className="w-4 h-4" />,
  [DocumentType.OTHER]: <Paperclip className="w-4 h-4" />,
}

interface Inspection {
  type: InspectionType
  completed: boolean
  photos: CapturedPhoto[]
  description: string
  timestamp: string
  vehicleDamaged: boolean
}

export function InspectionSheet({ dispatchId }: { dispatchId: string }) {
  const submitInspectionReport = useSubmitInspectionReport()
  const { driver } = useDriverStateV2()
  
  const [open, setOpen] = useState(false)
  const [isClosing, setIsClosing] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [inspections, setInspections] = useState<Record<InspectionType, Inspection>>({
    "pre-trip": {
      type: "pre-trip",
      completed: false,
      photos: [],
      description: "",
      timestamp: "",
      vehicleDamaged: false,
    },
    "post-trip": {
      type: "post-trip",
      completed: false,
      photos: [],
      description: "",
      timestamp: "",
      vehicleDamaged: false,
    },
  })
  const [selectedInspection, setSelectedInspection] = useState<InspectionType | null>(null)
  const [tempDescription, setTempDescription] = useState("")
  const [tempPhotos, setTempPhotos] = useState<CapturedPhoto[]>([])
  const [tempTimestamp, setTempTimestamp] = useState("")
  const [tempVehicleDamaged, setTempVehicleDamaged] = useState(false)
  const [showDocumentTypeSelector, setShowDocumentTypeSelector] = useState(false)
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const completedCount = Object.values(inspections).filter((i) => i.completed).length

  const handleSelectInspection = (type: InspectionType) => {
    setSelectedInspection(type)
    setTempDescription(inspections[type].description)
    setTempPhotos([...inspections[type].photos])
    setTempTimestamp(inspections[type].timestamp)
    setTempVehicleDamaged(inspections[type].vehicleDamaged)
  }

  const handleBack = () => {
    setSelectedInspection(null)
    setTempDescription("")
    setTempPhotos([])
    setTempTimestamp("")
    setTempVehicleDamaged(false)
  }

  const handleClose = () => {
    setIsClosing(true)
    setTimeout(() => {
      setOpen(false)
      setIsClosing(false)
    }, 200) // Match the fade-out duration
  }

  const handleSetNow = () => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const day = String(now.getDate()).padStart(2, "0")
    const hours = String(now.getHours()).padStart(2, "0")
    const minutes = String(now.getMinutes()).padStart(2, "0")
    setTempTimestamp(`${year}-${month}-${day}T${hours}:${minutes}`)
  }

  const handleAddPhotosClick = () => {
    // If selector is already open, dismiss it. Otherwise open it.
    if (showDocumentTypeSelector) {
      setShowDocumentTypeSelector(false)
    } else {
      setShowDocumentTypeSelector(true)
    }
  }

  const handleDocumentTypeSelected = (type: DocumentType) => {
    setSelectedDocumentType(type)
    // Trigger file input after selector is dismissed
    setTimeout(() => {
      fileInputRef.current?.click()
    }, 0)
  }

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    
    if (files && selectedDocumentType) {
      const currentDocumentType = selectedDocumentType
      // Convert FileList to actual array immediately to avoid closure issues
      const filesArray = Array.from(files)
      
      // Get driver's current location from state (cached when available)
      const latitude = driver?.location?.latitude ?? 0
      const longitude = driver?.location?.longitude ?? 0
      
      const captureTime = new Date().toISOString()

      const newPhotos = filesArray.map((file) => {
        const dataUrl = URL.createObjectURL(file)
        return {
          dataUrl,
          latitude,
          longitude,
          captureTime,
          documentType: currentDocumentType,
          file,
        } as CapturedPhoto
      })

      setTempPhotos((prevPhotos) => [...prevPhotos, ...newPhotos])
      setSelectedDocumentType(null)
    }
    // Reset file input
    e.target.value = ''
  }

  const handleRemovePhoto = (index: number) => {
    setTempPhotos(tempPhotos.filter((_, i) => i !== index))
  }

  const handleSaveInspection = async () => {
    if (selectedInspection && dispatchId) {
      setIsSubmitting(true)
      try {
        // First update local state
        setInspections({
          ...inspections,
          [selectedInspection]: {
            ...inspections[selectedInspection],
            photos: tempPhotos,
            description: tempDescription,
            timestamp: tempTimestamp,
            vehicleDamaged: tempVehicleDamaged,
            completed: tempPhotos.length > 0 || tempDescription.length > 0,
          },
        })

        // Then submit to backend
        const inspectionType = selectedInspection === "pre-trip" ? "ON_SITE" : "ON_DROP_OFF"
        
        await submitInspectionReport.mutateAsync({
          dispatchId,
          data: {
            inspectionType: inspectionType as "ON_SITE" | "ON_DROP_OFF",
            vehicleDamaged: tempVehicleDamaged,
            latitude: driver?.location?.latitude,
            longitude: driver?.location?.longitude,
            notes: tempDescription || "Default Note",
            photos: tempPhotos.map(photo => ({
              dataUrl: photo.dataUrl,
              latitude: photo.latitude,
              longitude: photo.longitude,
              captureTime: photo.captureTime,
              documentType: photo.documentType,
            })),
            documentIds: [],
          },
        })

        handleBack()
      } catch (error) {
        console.error("Error submitting inspection:", error)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const formatTimestamp = (timestamp: string) => {
    if (!timestamp) return null
    const date = new Date(timestamp)
    return date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const getInspectionLabel = (type: InspectionType) => {
    return type === "pre-trip" ? "On Site Inspection" : "Drop Off Inspection"
  }

  const getInspectionDescription = (type: InspectionType) => {
    return type === "pre-trip"
      ? "Document vehicle condition at pickup location"
      : "Document vehicle condition at drop off location"
  }

  if (!open) {
    return (
      <button
        onClick={() => {
          console.log('📋 Inspections button clicked, dispatchId:', dispatchId)
          setOpen(true)
        }}
        className="w-full h-auto py-4 px-4 text-left font-normal border-2 border-gray-300 hover:border-primary hover:bg-blue-50 transition-all bg-background rounded-lg flex items-center justify-between active:scale-95"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
            <ClipboardList className="w-5 h-5 text-primary" />
          </div>
          <div>
            <p className="font-semibold text-black">Inspections</p>
            <p className="text-xs text-gray-600">On site and drop off documentation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "text-sm font-semibold px-2.5 py-1 rounded-full",
              completedCount === 2
                ? "bg-green-100 text-green-700"
                : "bg-gray-100 text-gray-600"
            )}
          >
            {completedCount}/2
          </span>
        </div>
      </button>
    )
  }

  if (!selectedInspection) {
    return (
      <div className={cn(
        "fixed inset-0 bg-black bg-opacity-50 flex items-end z-50 animate-in fade-in duration-200",
        isClosing && "animate-out fade-out duration-200"
      )}>
        <div className={cn(
          "w-full bg-background rounded-t-3xl p-6 h-auto max-h-[70vh] overflow-y-auto flex flex-col animate-in slide-in-from-bottom-96 duration-300",
          isClosing && "animate-out slide-out-to-bottom-96 duration-300"
        )}>
          <div className="flex items-start justify-between mb-6">
            <div>
              <h2 className="text-xl font-semibold text-black">Vehicle Inspections</h2>
              <p className="text-sm text-gray-600 mt-1">
                Complete pre and post-trip inspections with photos and notes
              </p>
            </div>
            <button
              onClick={handleClose}
              className="text-2xl text-gray-400 hover:text-gray-600 flex-shrink-0 ml-4"
            >
              ×
            </button>
          </div>

          <div className="flex-1 overflow-y-auto space-y-3 mb-4">
            {(["pre-trip", "post-trip"] as InspectionType[]).map((type) => {
              const inspection = inspections[type]
              return (
                <button
                  key={type}
                  onClick={() => handleSelectInspection(type)}
                  className={cn(
                    "w-full p-4 rounded-lg border-2 transition-all text-left",
                    inspection.completed
                      ? "border-green-300 bg-green-50"
                      : "border-gray-200 bg-background hover:border-gray-300"
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-black">{getInspectionLabel(type)}</h3>
                        {inspection.completed && (
                          <div className="w-5 h-5 rounded-full bg-green-500 flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          </div>
                        )}
                      </div>
                      <p className="text-xs text-gray-600">{getInspectionDescription(type)}</p>
                        {inspection.completed && (
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-600">
                            {inspection.vehicleDamaged && (
                              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-red-100 text-red-700 font-medium">
                                ⚠ Damage Reported
                              </span>
                            )}
                            {inspection.photos.length > 0 && (
                              <span className="flex items-center gap-1">
                                <Camera className="w-3 h-3" />
                                {inspection.photos.length} {inspection.photos.length === 1 ? "photo" : "photos"}
                              </span>
                            )}
                            {inspection.description && <span>• Notes added</span>}
                            {inspection.timestamp && (
                              <>
                                <span>•</span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatTimestamp(inspection.timestamp)}
                                </span>
                              </>
                            )}
                          </div>
                        )}
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={cn(
      "fixed inset-0 bg-black bg-opacity-50 flex items-end z-50 animate-in fade-in duration-200",
      isClosing && "animate-out fade-out duration-200"
    )}>
      <div className={cn(
        "w-full bg-background rounded-t-3xl p-6 h-auto max-h-[85vh] overflow-y-auto flex flex-col animate-in slide-in-from-bottom-96 duration-300",
        isClosing && "animate-out slide-out-to-bottom-96 duration-300"
      )}>
        <div className="flex items-start justify-between mb-6">
          <div className="flex items-start gap-3">
            <button
              onClick={handleBack}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
            >
              <ChevronLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h2 className="text-xl font-semibold text-black">
                {getInspectionLabel(selectedInspection)}
              </h2>
              <p className="text-sm text-gray-600">{getInspectionDescription(selectedInspection)}</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="text-2xl text-gray-400 hover:text-gray-600 flex-shrink-0"
          >
            ×
          </button>
        </div>

        <div className="flex-1 overflow-y-auto space-y-6 mb-4">
          <div className="space-y-3">
            <label className="text-sm font-medium text-black block">Date & Time</label>
            <div className="space-y-2">
              <div className="relative">
                <input
                  type="datetime-local"
                  value={tempTimestamp}
                  onChange={(e) => setTempTimestamp(e.target.value)}
                  className="w-full h-12 px-4 pr-10 rounded-lg border-2 border-gray-300 bg-background text-black focus:border-primary focus:outline-none transition-colors"
                />
              </div>
              <button
                type="button"
                onClick={handleSetNow}
                className="w-full h-10 text-sm font-medium border-2 border-gray-300 bg-background text-gray-700 rounded-lg hover:bg-gray-50 transition-colors flex items-center justify-center gap-2"
              >
                <Clock className="w-4 h-4" />
                Set to Current Time
              </button>
            </div>
          </div>

          <div className="space-y-3">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={tempVehicleDamaged}
                onChange={(e) => setTempVehicleDamaged(e.target.checked)}
                className="w-5 h-5 border-2 border-gray-300 rounded bg-background text-red-600 focus:ring-primary cursor-pointer accent-red-600"
              />
              <span className="text-sm font-medium text-black">Vehicle Damaged</span>
            </label>
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-black block">Photos</label>
            <button
              type="button"
              onClick={handleAddPhotosClick}
              className="flex items-center justify-center gap-2 p-4 rounded-lg border-2 border-dashed border-gray-300 bg-gray-50 hover:border-primary/50 hover:bg-blue-50 transition-all cursor-pointer w-full"
            >
              <Camera className="w-5 h-5 text-gray-500" />
              <span className="text-sm font-medium text-gray-700">Add Photos</span>
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />

            {tempPhotos.length > 0 && (
              <div className="space-y-2">
                <div className="grid grid-cols-6 gap-2">
                  {tempPhotos.map((photo, index) => (
                    <div key={index} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 group">
                      <img
                        src={photo.dataUrl}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                        <div className="bg-black/70 text-white px-2 py-1 rounded flex items-center gap-1 text-xs font-medium opacity-0 group-hover:opacity-100 transition-opacity">
                          {DOCUMENT_TYPE_ICONS[photo.documentType]}
                          {DOCUMENT_TYPE_LABELS[photo.documentType]}
                        </div>
                      </div>
                      <button
                        onClick={() => handleRemovePhoto(index)}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center transition-colors"
                      >
                        <X className="w-3 h-3 text-white" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="text-xs text-gray-600 space-y-1">
                  {tempPhotos.map((photo, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="flex items-center gap-1 text-gray-600">
                        {DOCUMENT_TYPE_ICONS[photo.documentType]}
                        {DOCUMENT_TYPE_LABELS[photo.documentType]}
                      </span>
                      <span className="text-gray-400">•</span>
                      <span>📍 {photo.latitude.toFixed(4)}, {photo.longitude.toFixed(4)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-3">
            <label className="text-sm font-medium text-black block">Notes & Observations</label>
            <textarea
              placeholder="Document any damage, issues, or important details..."
              value={tempDescription}
              onChange={(e) => setTempDescription(e.target.value)}
              className="w-full min-h-[120px] px-4 py-3 border-2 border-gray-300 rounded-lg bg-background text-black placeholder-gray-400 focus:border-primary focus:outline-none transition-colors resize-none"
            />
          </div>
        </div>

        <button
          onClick={handleSaveInspection}
          disabled={isSubmitting || (tempPhotos.length === 0 && tempDescription.length === 0)}
          className="w-full bg-primary text-primary-foreground py-3 px-4 rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              Submitting...
            </>
          ) : (
            `Submit ${getInspectionLabel(selectedInspection)}`
          )}
        </button>
      </div>

      <DocumentTypeSelector
        isOpen={showDocumentTypeSelector}
        onSelect={handleDocumentTypeSelected}
        onDismiss={() => setShowDocumentTypeSelector(false)}
      />
    </div>
  )
}

